import { Injectable } from '@angular/core';
import { Observable, defer, throwError, timer } from 'rxjs';
import { catchError, map, retryWhen, mergeMap } from 'rxjs/operators';
import { CapacitorHttp, HttpResponse } from '@capacitor/core';
import { environment } from 'src/environments/environment';
import { WindowPayload } from './window-payload-builder.service';

/* ===================== TYPES ===================== */

export interface WinterCalcRequest {
  project?: string;
  externalTemp: number;
  internalTemp: number;
  cost: number;
  currency: string;
  efficiency: number;
  costDelta: number;
  window1: WindowPayload;
  window2: WindowPayload;
  windowsCount: number;
  yearsCount: number;
  heatSeason: number;
  heatingFuels: string;
  heatingUnit: string;
  calorific: number;
}

export interface SummerCalcRequest {
  project?: string;
  externalTemp: number;
  internalTemp: number;
  cost: number;
  currency: string;
  efficiency: number;
  costDelta: number;
  window1: WindowPayload;
  window2: WindowPayload;
  windowsCount: number;
  yearsCount: number;
  coolSeason: number;
  power: string;
}

export interface WinterSummerCalcRequest {
  winterData: WinterCalcRequest;
  summerData: SummerCalcRequest;
  window1: WindowPayload;
  window2: WindowPayload;
}

export interface WinterCalcResponse {
  diffHeatRequirements: number;
  diffHeatDemand: number;
  diffHeatCosts: number;
  diffCO2: number;
}

export interface SummerCalcResponse {
  diffCoolRequirements: number;
  diffCoolCosts: number;
  diffCO2: number;
}

export interface WinterSummerCalcResponse {
  diffHeatCoolRequirements: number;
  diffCosts: number;
  diffCO2: number;
  winter: WinterCalcResponse;
  summer: SummerCalcResponse;
}

/* ===================== SERVICE ===================== */
@Injectable({ providedIn: 'root' })
export class EnergySavingsApiService {
  private readonly baseUrl = environment.caluwin_api.base_url;
  private readonly RETRY_COUNT = 2;
  private readonly BASE_DELAY_MS = 200;

  /* ===================== PUBLIC API ===================== */
  
  calculateWinter(params: WinterCalcRequest): Observable<WinterCalcResponse> {
    return this.makeRequest<WinterCalcResponse>(
      `${this.baseUrl}/api/EnergySavings/winter-calc`,
      params
    );
  }

  calculateSummer(params: SummerCalcRequest): Observable<SummerCalcResponse> {
    return this.makeRequest<SummerCalcResponse>(
      `${this.baseUrl}/api/EnergySavings/summer-calc`,
      params
    );
  }

  calculateWinterSummer(params: WinterSummerCalcRequest): Observable<WinterSummerCalcResponse> {
    return this.makeRequest<WinterSummerCalcResponse>(
      `${this.baseUrl}/api/EnergySavings/wintersummer-calc`,
      params
    );
  }

  /* ===================== HELPERS ===================== */
  
  private makeRequest<T>(url: string, data: any): Observable<T> {
    return defer(() =>
      CapacitorHttp.post({
        url,
        method: 'POST',
        headers: this.getHeaders(),
        data,
      })
    ).pipe(
      map(this.handleSuccessResponse<T>),
      retryWhen(this.retryWithBackoff()),
      catchError(err => throwError(() => this.handleFinalError(err)))
    );
  }

  private getHeaders(): Record<string, string> {
    return {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    };
  }

  private handleSuccessResponse<T>(response: HttpResponse): T {
    if (response.status < 200 || response.status >= 300) {
      throw response;
    }
    return response.data as T;
  }

  /* ===================== RETRY LOGIC ===================== */
  
  private retryWithBackoff() {
    return (errors: Observable<any>) =>
      errors.pipe(
        mergeMap((error, retryIndex) => {
          const attempt = retryIndex + 1;
          if (
            attempt > this.RETRY_COUNT ||
            !this.isRetryableError(error?.status)
          ) {
            return throwError(() => error);
          }
          const delayTime = Math.pow(2, attempt) * this.BASE_DELAY_MS;
          return timer(delayTime);
        })
      );
  }

  private isRetryableError(status?: number): boolean {
    return [0, 408, 429, 500, 502, 503, 504].includes(status ?? 0);
  }

  /* ===================== ERROR HANDLING ===================== */
  
  private handleFinalError(error: any): Error {
    if (typeof error?.status === 'number') {
      return this.handleHttpError(error.status, error.data);
    }
    return this.handleNetworkError(error);
  }

  private handleHttpError(status: number, data: any): Error {
    const messageMap: Record<number, string> = {
      400: 'Invalid request parameters.',
      401: 'Unauthorized. Please login again.',
      403: 'Access forbidden.',
      404: 'API endpoint not found.',
      422: 'Validation error. Please check your inputs.',
      429: 'Too many requests. Please try again later.',
      500: 'Server error. Please try again later.',
      502: 'Service temporarily unavailable.',
      503: 'Service temporarily unavailable.',
      504: 'Service temporarily unavailable.',
    };
    const message = data?.message || messageMap[status] || `Error ${status}`;
    console.error('API Error:', status, data);
    return new Error(message);
  }

  private handleNetworkError(error: any): Error {
    const message =
      error?.message?.includes('timeout')
        ? 'Request timeout. Please try again.'
        : 'Network error. Please check your connection.';
    console.error('Network Error:', error);
    return new Error(message);
  }
}
